import React from 'react'

interface DataListProps<Row> {
  rows: Row[]
  getRowKey: (row: Row) => string
  renderItem: (row: Row) => React.ReactNode
  actions?: (row: Row) => React.ReactNode
  onItemClick?: (row: Row) => void
  divider?: 'thin' | 'thick'
  isLoading?: boolean
  loadingRows?: number
  empty?: React.ReactNode
  'data-testid'?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataList = <Row extends Record<string, any>>({
  rows,
  getRowKey,
  renderItem,
  actions,
  onItemClick,
  divider = 'thin',
  isLoading,
  loadingRows = 4,
  empty,
  'data-testid': testId,
}: DataListProps<Row>) => {
  const dividerClass = divider === 'thick' ? 'border-b-2' : 'border-b'

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid={testId}>
        {Array.from({ length: loadingRows }).map((_, idx) => (
          <div key={idx} className="h-16 bg-gray-100 rounded-sm p-4 animate-pulse" />
        ))}
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-sm text-gray-500 text-center italic" data-testid={testId}>
        {empty || 'No items'}
      </div>
    )
  }

  return (
    <div
      className="divide-y divide-gray-200 bg-white rounded-lg border border-gray-200 overflow-hidden"
      role="list"
      data-testid={testId}
    >
      {rows.map((row) => (
        <div
          key={getRowKey(row)}
          role="listitem"
          data-key={getRowKey(row)}
          onClick={() => onItemClick?.(row)}
          className={`flex items-center justify-between gap-4 px-4 py-3 ${onItemClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${dividerClass} last:border-b-0 border-gray-200`}
        >
          <div className="flex-1 min-w-0">{renderItem(row)}</div>
          {actions && <div className="shrink-0">{actions(row)}</div>}
        </div>
      ))}
    </div>
  )
}

export default DataList
